import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { PremiumProvider } from './context/PremiumContext.jsx';
import { GameProvider } from './context/GameContext.jsx';
import TabBar from './components/TabBar.jsx';
import InstallPrompt from './components/InstallPrompt.jsx';

import Home from './screens/Home.jsx';
import Login from './screens/Login.jsx';
import CreateGame from './screens/CreateGame.jsx';
import GameSetup from './screens/GameSetup.jsx';
import Scorecard from './screens/Scorecard.jsx';
import EndGame from './screens/EndGame.jsx';
import JoinGame from './screens/JoinGame.jsx';
import ClaimPlayer from './screens/ClaimPlayer.jsx';
import Profile from './screens/Profile.jsx';
import Friends from './screens/Friends.jsx';
import AddFriend from './screens/AddFriend.jsx';
import Community from './screens/Community.jsx';
import Stories from './screens/Stories.jsx';
import AddStory from './screens/AddStory.jsx';
import Premium from './screens/Premium.jsx';
import PastGames from './screens/PastGames.jsx';
import Rules from './screens/Rules.jsx';
import NotFound from './screens/NotFound.jsx';

// Admin pulls in Recharts (~100 KB gzipped) so it ships as its own chunk and
// only loads when an admin actually visits /admin.
const Admin = lazy(() => import('./screens/Admin.jsx'));

function AdminFallback() {
  return (
    <div className="screen center">
      <div className="loading-state">
        <div className="spinner" />
        <span className="loading-state-label">Loading admin…</span>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function Shell() {
  const loc = useLocation();
  // Hide tab bar on fullscreen-ish screens
  const hideTabBar =
    loc.pathname.startsWith('/login') ||
    /^\/game\/[^/]+$/.test(loc.pathname) ||
    /^\/game\/[^/]+\/results$/.test(loc.pathname) ||
    /^\/game\/[^/]+\/claim$/.test(loc.pathname) ||
    loc.pathname.startsWith('/setup') ||
    loc.pathname.startsWith('/create') ||
    loc.pathname.startsWith('/stories/new') ||
    loc.pathname.startsWith('/rules') ||
    loc.pathname.startsWith('/admin');

  return (
    <div className="app-shell">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/create" element={<RequireAuth><CreateGame /></RequireAuth>} />
        <Route path="/setup" element={<RequireAuth><GameSetup /></RequireAuth>} />
        <Route path="/join" element={<RequireAuth><JoinGame /></RequireAuth>} />
        <Route path="/game/:code" element={<RequireAuth><Scorecard /></RequireAuth>} />
        <Route path="/game/:code/claim" element={<RequireAuth><ClaimPlayer /></RequireAuth>} />
        <Route path="/game/:code/results" element={<RequireAuth><EndGame /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        <Route path="/friends" element={<RequireAuth><Friends /></RequireAuth>} />
        <Route path="/friends/add" element={<RequireAuth><AddFriend /></RequireAuth>} />
        <Route path="/community" element={<RequireAuth><Community /></RequireAuth>} />
        <Route path="/stories" element={<RequireAuth><Stories /></RequireAuth>} />
        <Route path="/stories/new" element={<RequireAuth><AddStory /></RequireAuth>} />
        <Route path="/premium" element={<RequireAuth><Premium /></RequireAuth>} />
        <Route path="/games" element={<RequireAuth><PastGames /></RequireAuth>} />
        <Route path="/rules" element={<Rules />} />
        {/* Admin is deliberately NOT wrapped in RequireAuth —
            it has its own password gate using the admin key.
            Lazy-loaded so Recharts doesn't ship in the main bundle. */}
        <Route
          path="/admin"
          element={
            <Suspense fallback={<AdminFallback />}>
              <Admin />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideTabBar && <TabBar />}
      <InstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PremiumProvider>
          <GameProvider>
            <Shell />
          </GameProvider>
        </PremiumProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
